/**
 * frontend/src/components/SecurityBanners.jsx
 * =============================================
 * Two components driven by the API event system in useApiEvents.js:
 *
 * RateLimitBanner
 *   Slides in at the top of the dashboard when a 429 fires.
 *   Shows a live countdown (seconds) until requests are allowed again.
 *   Auto-dismisses when the countdown hits zero.
 *
 * PlanUpgradeModal
 *   Full-screen modal when a plan gate 403 fires.
 *   Names the feature that was blocked, the current plan, and the
 *   minimum plan required. Links to the Stripe Billing Portal.
 *
 * Both are rendered inside ManagerLayout so they only appear on the
 * manager dashboard surface, not on /checkin.
 */

import { useMutation } from '@tanstack/react-query'
import { billingApi } from '../lib/api'

// ─── Plan display helpers ─────────────────────────────────────────────────

const PLAN_LABELS = {
  trial:   'Trial',
  starter: 'Starter',
  growth:  'Growth',
  pro:     'Pro',
}

const PLAN_ORDER  = ['trial', 'starter', 'growth', 'pro']
const PLAN_PRICES = { starter: '$49', growth: '$99', pro: '$199' }

function PlansAbove(currentPlan) {
  const idx = PLAN_ORDER.indexOf(currentPlan)
  return PLAN_ORDER.slice(idx + 1)
}

// ─── Rate limit banner ────────────────────────────────────────────────────

export function RateLimitBanner({ banner }) {
  if (!banner || banner.secondsLeft <= 0) return null

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center
                    bg-amber-500/95 backdrop-blur-sm px-4 py-2.5 shadow-lg
                    animate-in slide-in-from-top duration-200">
      <div className="flex items-center gap-3 text-amber-950 text-sm font-medium">
        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd" />
        </svg>
        <span>
          Too many requests — please wait{' '}
          <span className="font-bold tabular-nums">{banner.secondsLeft}s</span>{' '}
          before trying again.
        </span>
      </div>
    </div>
  )
}

// ─── Plan upgrade modal ───────────────────────────────────────────────────

export function PlanUpgradeModal({ modal, onClose }) {
  if (!modal) return null

  const upgradePlans = PlansAbove(modal.current_plan)

  const portalMutation = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: (res) => {
      window.open(res.data.url, '_blank')
      onClose()
    },
  })

  const checkoutMutation = useMutation({
    mutationFn: (tier) => billingApi.checkout(tier),
    onSuccess: (res) => {
      window.location.href = res.data.url
    },
  })

  const FEATURE_NAMES = {
    PLAN_UPGRADE_REQUIRED: 'This feature',
    PARTNER_LIMIT_REACHED: 'Adding more partners',
    TOKEN_LIMIT_REACHED:   'Adding more staff tokens',
  }
  const featureName = FEATURE_NAMES[modal.code] || 'This feature'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md shadow-2xl
                      animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center border-b border-gray-800">
          <div className="text-3xl mb-3">🚀</div>
          <h2 className="text-lg font-bold text-white">Upgrade to unlock this feature</h2>
          <p className="text-sm text-gray-400 mt-2 leading-relaxed">
            {featureName} requires the{' '}
            <span className="text-violet-400 font-semibold capitalize">
              {PLAN_LABELS[modal.required_plan] || modal.required_plan}
            </span>{' '}
            plan or higher.
          </p>
          {modal.message && modal.code !== 'PLAN_UPGRADE_REQUIRED' && (
            <p className="text-xs text-gray-500 mt-1">{modal.message}</p>
          )}
        </div>

        {/* Plan cards */}
        <div className="px-6 py-5 space-y-3">
          {upgradePlans.map((tier) => {
            const isMinimum = tier === modal.required_plan
            return (
              <button
                key={tier}
                onClick={() => checkoutMutation.mutate(tier)}
                disabled={checkoutMutation.isPending}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl
                            border transition-all text-left
                            ${isMinimum
                              ? 'border-violet-500 bg-violet-700/20 hover:bg-violet-700/30'
                              : 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'}
                            disabled:opacity-50`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold capitalize">
                      {PLAN_LABELS[tier]}
                    </span>
                    {isMinimum && (
                      <span className="text-xs bg-violet-600 text-white px-2 py-0.5 rounded-full font-medium">
                        Recommended
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {tier === 'starter' && '25 partners · 5 staff tokens · 30 day history'}
                    {tier === 'growth'  && '100 partners · 25 staff tokens · 1 year history · CSV export'}
                    {tier === 'pro'     && 'Unlimited everything · API access'}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className="text-white font-bold">{PLAN_PRICES[tier] || '—'}</div>
                  <div className="text-xs text-gray-500">/mo</div>
                </div>
              </button>
            )
          })}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-2">
          {/* If they already have a subscription, link to portal */}
          <button
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isPending}
            className="w-full py-2 text-sm text-gray-400 hover:text-white
                       border border-gray-700 rounded-xl transition-colors
                       disabled:opacity-50"
          >
            {portalMutation.isPending ? 'Loading…' : 'Manage existing subscription'}
          </button>

          <button
            onClick={onClose}
            className="w-full py-2 text-sm text-gray-600 hover:text-gray-400 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  )
}
